import { GuestsType } from 'types/mappedProductData';
import { resolvePdfGuideUrl } from 'utils/pdfGuideUrl';

import { DEFAULT_PARTNER_KEY } from './constants';
import {
  sourceCodesWithElevatedPermission,
  sourceCodesWithStandardPermission,
} from './features';

export enum GUEST_TYPES {
  adults = 'adults',
  children = 'children',
  infants = 'infants',
  seniors = 'seniors',
}

export const DEFAULT_MAX_BOOKING_PERIOD = 9999;
export const DEFAULT_MIN_BOOKING_PERIOD = 48;
export const DEFAULT_CANCELLATION_PERIOD = 48;
export const DEFAULT_AMEND_PERIOD = 48;
export const DEFAULT_MAX_PARTY_SIZE = 5;
export const DEFAULT_TIME_SLOT_PERIOD = 15;
export const DEFAULT_CURRENCY = 'GBP';
export const DEFAULT_PRICE = 6;
const DEFAULT_VIEW_ONLY = false;
const DEFAULT_LEGACY_PRODUCT = false;

export const DEFAULT_GUESTS_PARAMS = {
  [GUEST_TYPES.adults]: {
    age: '12+',
    isChargeable: true,
    isPartySizeImpacted: true,
    maxCountPerGuestType: 5,
    price: DEFAULT_PRICE,
  },
  [GUEST_TYPES.children]: {
    age: '2‑11',
    isChargeable: true,
    isPartySizeImpacted: true,
    maxCountPerGuestType: 5,
    price: DEFAULT_PRICE,
  },
  [GUEST_TYPES.infants]: {
    age: '0‑2',
    isChargeable: false,
    isPartySizeImpacted: false,
    maxCountPerGuestType: 5,
    price: 0,
  },
  [GUEST_TYPES.seniors]: {
    age: '65+',
    isChargeable: false,
    isPartySizeImpacted: true,
    maxCountPerGuestType: 5,
    price: DEFAULT_PRICE,
  },
};

const DEFAULT_GUESTS: GuestsType = {
  [GUEST_TYPES.adults]: DEFAULT_GUESTS_PARAMS[GUEST_TYPES.adults],
  [GUEST_TYPES.children]: DEFAULT_GUESTS_PARAMS[GUEST_TYPES.children],
  [GUEST_TYPES.infants]: DEFAULT_GUESTS_PARAMS[GUEST_TYPES.infants],
};

export const FAQ_PP = 'https://memberhelp.prioritypass.com/en/support/home';
export const FAQ_LK = 'https://www.loungekey.com/en/contact-us';
export const FAQ_AMEX_PREBOOK_ENTITLEMENTS =
  'https://prioritypass.com/pre-book-entitlements';
export const DEFAULT_WEB_REFERRER = 'https://www.prioritypass.com';

export const DEFAULT_CONTEXT_PRODUCT_VALUE = {
  accessPeriod: 3,
  airportCode: '',
  airportName: '',
  bookingMaxPeriodAllowed: DEFAULT_MAX_BOOKING_PERIOD,
  bookingMinPeriodAllowed: DEFAULT_MIN_BOOKING_PERIOD,
  cancellationPeriod: DEFAULT_CANCELLATION_PERIOD,
  currency: DEFAULT_CURRENCY,
  guests: DEFAULT_GUESTS,
  imageUrl: '',
  legacyProduct: DEFAULT_LEGACY_PRODUCT,
  loungeCode: '',
  loungeID: '',
  loungeName: '',
  maxPartySize: DEFAULT_MAX_PARTY_SIZE,
  partnerID: '',
  partnerProductID: DEFAULT_PARTNER_KEY,
  pdfGuideUrl: resolvePdfGuideUrl(process.env.NEXT_PUBLIC_PDF_GUIDE_PATH) || '',
  productID: '',
  saleprice: DEFAULT_PRICE,
  stripeID: '',
  terminal: '',
  timeSlotPeriod: DEFAULT_TIME_SLOT_PERIOD,
  timezone: '',
  validPrebookPeriod: true,
  viewOnly: DEFAULT_VIEW_ONLY,
};

export const FASTTRACK_IMAGE_URL =
  'https://images.ctfassets.net/687qsr16btly/48U7Yq5FMs6hxoecana05J/3961622dbc56f0a4cdd4667a9248fb9c/fast-track.jpeg?w=500&fm=webp&q=80';

export const FASTTRACK_CONSENT_IMAGE_URL =
  'https://images.ctfassets.net/687qsr16btly/6QY9B4srkMlpKr3wyaQqAr/f0369f0950053175928427314ca5b4a1/GettyImages-2192658096.jpg';

export const FASTTRACK_CONSENT_TERMS_URL =
  'https://www.prioritypass.com/en-GB/conditions-of-use';

const CAVU_PARTNER_ID = '007';

const HIGHPASS_PARTNER_ID = '009';

const ALWAYS_AVAILABLE_SUPPLIER_IDS = [CAVU_PARTNER_ID, HIGHPASS_PARTNER_ID];

const ALWAYS_AVAILABLE_SUPPLIER_IDS_SET = new Set(
  ALWAYS_AVAILABLE_SUPPLIER_IDS
);

export const isAlwaysAvailableSupplier = (supplierId?: string): boolean =>
  !!supplierId && ALWAYS_AVAILABLE_SUPPLIER_IDS_SET.has(supplierId);

export const ELEVATED_LOUNGES = [
  'GLA5',
  'LGW18',
  'LGW19',
  'LHR34',
  'MAN13',
  'BHX7',
];

export const PROHIBITED_NATIONALS_FT_AIRPORT_CODES = [
  'HAN',
  'SGN',
  'DAD',
  'CXR',
];

export const isProhibitedNationalsFTLocation = (
  loungeCode?: string,
  isLocationProduct?: boolean
): boolean => {
  if (!loungeCode || !isLocationProduct) return false;
  const airportCode = loungeCode.replace(/\d+FT$/, '');
  return PROHIBITED_NATIONALS_FT_AIRPORT_CODES.includes(airportCode);
};

const PLATINUM_SOURCECODES = [
  'LXPCCEH3BGBG',
  'LXPCBEA4BGBG',
  'TCGAXCOMPS',
  'TCGAXCOMPS2',
  'TCGAXCOMPS3',
];
const CENTURION_SOURCECODES = ['LXCCCEH3BGBG', 'LXCCCEH3SGBG', 'TCGAXCOMPS1'];
const FAST_TRACK_SOURCECODES = ['PRODPBENTVAL', 'PRODFTENTVAL', 'PRODPBFTENT'];

const PLATINUM_SOURCECODES_TEST = [
  'BAASTESTFB',
  'LXPCCEH3BGBG1',
  'DEYAN',
  'LXPCCSD4SESP',
];
const CENTURION_SOURCECODES_TEST = [
  'LXCCCDH3BNLG',
  'LXCCEH3BGBG',
  'LXCCCEH3SGBG1',
  'BAASTEST',
];

export const FALLBACK_SOURCECODE_WITH_STANDARD_PERMISSION = [
  ...PLATINUM_SOURCECODES,
  ...CENTURION_SOURCECODES,
  ...FAST_TRACK_SOURCECODES,
  ...PLATINUM_SOURCECODES_TEST,
  ...CENTURION_SOURCECODES_TEST,
];
export const FALLBACK_SOURCECODE_WITH_ELEVATED_PERMISSION = [
  ...CENTURION_SOURCECODES,
  ...CENTURION_SOURCECODES_TEST,
];

export function getSourceCodesWithStandardPermission() {
  if (sourceCodesWithStandardPermission.length > 0) {
    return sourceCodesWithStandardPermission;
  }
  return FALLBACK_SOURCECODE_WITH_STANDARD_PERMISSION;
}

export function getSourceCodesWithElevatedPermission() {
  if (sourceCodesWithElevatedPermission.length > 0) {
    return sourceCodesWithElevatedPermission;
  }
  return FALLBACK_SOURCECODE_WITH_ELEVATED_PERMISSION;
}
